import { useDotLegalMutation, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { useOptimisticUpdate } from "../../../common/hooks/useOptimisticUpdate";
import { put } from "../../../common/api/apiShared";

import { DotLegalSelectOption } from "../../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { useTranslation } from "@localization/useTranslation";
import { PermissionLevel } from "../../Roles.types";
import { getStandardFieldTranslationKey } from "../../../entityTemplates/EntityTemplate.types";
import { FieldPermissionsProps } from "./FieldPermissions.tsx";

interface FieldPermissionOverride {
    standardFieldIdentifier: number | null;
    customInputFieldId: string | null;
    isHidden: boolean;
    permissionLevel: number | null;
    subPermissions: number[];
}

interface StandardFieldItem {
    identifier: number;
    name: string;
}

interface CustomFieldItem {
    id: string;
    name: string;
}

interface FieldSubPermissionItem {
    field: number;
    subPermissions: number[];
}

interface FeatureGatedField {
    field: number;
    tooltipKey: string;
}

interface FieldPermissionsViewModel {
    overrides: FieldPermissionOverride[];
    allStandardFields: StandardFieldItem[];
    allCustomFields: CustomFieldItem[];
    featureGatedFields: FeatureGatedField[];
    availableSubPermissions: FieldSubPermissionItem[];
    defaultPermissionLevel: number;
}

interface FieldPermissionsSaveModel {
    overrides: FieldPermissionOverride[];
}

export interface FieldPermissionRow {
    key: string;
    name: string;
    standardFieldIdentifier: number | null;
    customInputFieldId: string | null;
    featureGatedTooltipKey: string | null;
    availableSubPermissionTypes: number[];
}

export function useFieldPermissions(props: FieldPermissionsProps) {
    const { roleId, readOnly, templateEntityType } = props;
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();
    const { translateString, translatePermissionTypes } = useTranslation();

    const queryKey = `/roles/${roleId}/field-permissions?templateEntityType=${templateEntityType}`;
    const { isLoading, data } = useDotLegalQueryWithKey<FieldPermissionsViewModel>(queryKey, queryKey);

    const saveMutation = useDotLegalMutation((model: FieldPermissionsSaveModel) =>
        put<{}>(`/roles/${roleId}/field-permissions?templateEntityType=${templateEntityType}`, model)
    );

    const overrides = data?.overrides ?? [];

    const featureGatedMap = new Map<number, string>();
    for (const gated of data?.featureGatedFields ?? []) {
        featureGatedMap.set(gated.field, gated.tooltipKey);
    }

    const subPermissionsByField = new Map<number, number[]>();
    for (const sp of data?.availableSubPermissions ?? []) {
        subPermissionsByField.set(sp.field, sp.subPermissions);
    }

    const fields: FieldPermissionRow[] = buildFieldRows();

    function buildFieldRows(): FieldPermissionRow[] {
        if (!data) return [];

        const standardRows: FieldPermissionRow[] = data.allStandardFields.map((f) => ({
            key: `standard-${f.identifier}`,
            name: translateString(getStandardFieldTranslationKey(f.identifier)),
            standardFieldIdentifier: f.identifier,
            customInputFieldId: null,
            featureGatedTooltipKey: featureGatedMap.get(f.identifier) ?? null,
            availableSubPermissionTypes: subPermissionsByField.get(f.identifier) ?? [],
        }));

        const customRows: FieldPermissionRow[] = data.allCustomFields.map((f) => ({
            key: `custom-${f.id}`,
            name: f.name,
            standardFieldIdentifier: null,
            customInputFieldId: f.id,
            featureGatedTooltipKey: null,
            availableSubPermissionTypes: [],
        }));

        return [...standardRows, ...customRows];
    }

    function findOverride(row: FieldPermissionRow): FieldPermissionOverride | undefined {
        return overrides.find(
            (o) =>
                (row.standardFieldIdentifier !== null && o.standardFieldIdentifier === row.standardFieldIdentifier) ||
                (row.customInputFieldId !== null && o.customInputFieldId === row.customInputFieldId)
        );
    }

    function isHidden(row: FieldPermissionRow) {
        const override = findOverride(row);
        return override?.isHidden ?? false;
    }

    const defaultLevel = data?.defaultPermissionLevel ?? PermissionLevel.Edit;
    const isMasterReadOnly = defaultLevel < PermissionLevel.Edit;

    function effectiveLevel(row: FieldPermissionRow): PermissionLevel | null {
        const override = findOverride(row);
        if (override?.isHidden) return null;
        const overriddenLevel = override?.permissionLevel ?? defaultLevel;
        return Math.min(overriddenLevel, defaultLevel);
    }

    function isRead(row: FieldPermissionRow) {
        const level = effectiveLevel(row);
        return level !== null && level >= PermissionLevel.Read;
    }

    function isEdit(row: FieldPermissionRow) {
        const level = effectiveLevel(row);
        return level !== null && level >= PermissionLevel.Edit;
    }

    function isFeatureGated(row: FieldPermissionRow) {
        return row.featureGatedTooltipKey !== null;
    }

    function getFeatureGatedTooltip(row: FieldPermissionRow): string | undefined {
        return row.featureGatedTooltipKey ? translateString(row.featureGatedTooltipKey) : undefined;
    }

    function isFieldDisabled(row: FieldPermissionRow) {
        return readOnly || isFeatureGated(row);
    }

    async function save(newOverrides: FieldPermissionOverride[]) {
        if (!data) return;

        const updated: FieldPermissionsViewModel = { ...data, overrides: newOverrides };
        await putOnQueueAndSetQueryData(updated, queryKey, saveMutation.mutateAsync, {
            overrides: newOverrides,
        });
    }

    function buildOverride(row: FieldPermissionRow, isHiddenValue: boolean, level: number | null, subPerms: number[]): FieldPermissionOverride {
        return {
            standardFieldIdentifier: row.standardFieldIdentifier,
            customInputFieldId: row.customInputFieldId,
            isHidden: isHiddenValue,
            permissionLevel: isHiddenValue ? null : level,
            subPermissions: subPerms,
        };
    }

    function replaceOrAddOverride(currentOverrides: FieldPermissionOverride[], row: FieldPermissionRow, newOverride: FieldPermissionOverride) {
        const filtered = currentOverrides.filter(
            (o) =>
                !(row.standardFieldIdentifier !== null && o.standardFieldIdentifier === row.standardFieldIdentifier) &&
                !(row.customInputFieldId !== null && o.customInputFieldId === row.customInputFieldId)
        );
        return [...filtered, newOverride];
    }

    function removeOverride(currentOverrides: FieldPermissionOverride[], row: FieldPermissionRow) {
        return currentOverrides.filter(
            (o) =>
                !(row.standardFieldIdentifier !== null && o.standardFieldIdentifier === row.standardFieldIdentifier) &&
                !(row.customInputFieldId !== null && o.customInputFieldId === row.customInputFieldId)
        );
    }

    async function toggleRead(row: FieldPermissionRow) {
        if (isFieldDisabled(row)) return;

        const currentOverride = findOverride(row);

        if (currentOverride?.isHidden) {
            const newOverride = buildOverride(row, false, defaultLevel, row.availableSubPermissionTypes);
            await save(replaceOrAddOverride(overrides, row, newOverride));
        } else if (isRead(row)) {
            const newOverride = buildOverride(row, true, null, []);
            await save(replaceOrAddOverride(overrides, row, newOverride));
        }
    }

    async function toggleEdit(row: FieldPermissionRow) {
        if (isFieldDisabled(row) || isMasterReadOnly) return;

        const currentOverride = findOverride(row);
        const subPerms = currentOverride?.subPermissions ?? row.availableSubPermissionTypes;

        if (isEdit(row)) {
            await save(replaceOrAddOverride(overrides, row, buildOverride(row, false, PermissionLevel.Read, subPerms)));
        } else {
            await save(replaceOrAddOverride(overrides, row, buildOverride(row, false, PermissionLevel.Edit, subPerms)));
        }
    }

    async function onSelectAllEditChanged() {
        if (readOnly || isMasterReadOnly || !data) return;

        const featureGatedOverrides = overrides.filter((o) => o.standardFieldIdentifier !== null && featureGatedMap.has(o.standardFieldIdentifier));

        const allEdit = fields.filter((f) => !isFeatureGated(f)).every((f) => isEdit(f));
        if (allEdit) {
            const hideOverrides = fields.filter((f) => !isFeatureGated(f)).map((f) => buildOverride(f, true, null, []));
            await save([...featureGatedOverrides, ...hideOverrides]);
        } else {
            await save(featureGatedOverrides);
        }
    }

    function getHeaderCheckBoxState(): "true" | "false" | "indeterminate" {
        if (fields.length === 0) return "false";
        const editableFields = fields.filter((f) => !isFeatureGated(f));
        const allEdit = editableFields.every((f) => isEdit(f));
        const noneEdit = editableFields.every((f) => isHidden(f));
        if (allEdit) return "true";
        if (noneEdit) return "false";
        return "indeterminate";
    }

    function getSubPermissionOptions(row: FieldPermissionRow): DotLegalSelectOption[] {
        return row.availableSubPermissionTypes.map((type) => ({
            id: type.toString(),
            name: translatePermissionTypes(type.toString()),
        }));
    }

    function getChosenSubPermissions(row: FieldPermissionRow): string[] {
        const override = findOverride(row);
        if (!override) return row.availableSubPermissionTypes.map((sp) => sp.toString());
        return override.subPermissions.map((sp) => sp.toString());
    }

    async function setSubPermissions(row: FieldPermissionRow, values: string[]) {
        if (isFieldDisabled(row)) return;

        const currentOverride = findOverride(row);
        const newSubPerms = values.map(Number);
        const level = currentOverride?.permissionLevel ?? defaultLevel;
        const hidden = currentOverride?.isHidden ?? false;

        const newOverride = buildOverride(row, hidden, level, newSubPerms);

        await save(replaceOrAddOverride(overrides, row, newOverride));
    }

    return {
        isLoading,
        fields,
        isHidden,
        isRead,
        isEdit,
        isFieldDisabled,
        isFeatureGated,
        getFeatureGatedTooltip,
        toggleRead,
        toggleEdit,
        onSelectAllEditChanged,
        headerCheckBoxState: getHeaderCheckBoxState(),
        getSubPermissionOptions,
        getChosenSubPermissions,
        setSubPermissions,
        isMasterReadOnly,
    };
}
